import * as yup from "yup";
import i18next from "../../../i18n";
import { Currency, PurchaseRequestFilePayload } from "../../../types";
import type { ItemRow } from "./types";
import { isItemComplete } from "./itemStatus";

/**
 * The React Hook Form value shape for the whole New-Purchase-Request page — the request header
 * fields, the request-wide "apply to all items" defaults, and the per-item `useFieldArray` rows.
 * One `useForm<RequestFormValues>()` instance is the single store the Carbon migration replaced the
 * page's dozen `useState` hooks with.
 */
export interface RequestFormValues {
  name: string;
  purchaseRequestCode: string;
  comments: string;
  useGlobalBudget: boolean;
  globalCurrency: Currency;
  globalBudgetValue: number | null;
  useGlobalDates: boolean;
  singleDateMode: boolean;
  globalNeededFrom: string;
  globalNeededTo: string;
  files: PurchaseRequestFilePayload[];
  items: ItemRow[];
}

/** The empty form the New-Purchase-Request page seeds RHF with. */
export const emptyRequestFormValues: RequestFormValues = {
  name: "",
  purchaseRequestCode: "",
  comments: "",
  useGlobalBudget: true,
  globalCurrency: Currency.USD,
  globalBudgetValue: null,
  useGlobalDates: true,
  singleDateMode: true,
  globalNeededFrom: "",
  globalNeededTo: "",
  files: [],
  items: [],
};

/**
 * Submit-gate for creating a purchase request. Mutation-proven in
 * `requestFormSchema.test.ts` (rejects an empty name, rejects zero items, rejects an incomplete
 * item, accepts a complete one).
 *
 * The item-completeness rule is DELEGATED to `itemStatus.ts#isItemComplete` — the single source of
 * truth the row status chip, the header summary line and this gate all read (per the flow's own
 * ruling that completeness rules are never re-derived locally). This schema encodes only the two
 * request-level rules the pre-Carbon page's `canSubmit` added on top of per-item completeness:
 * a non-blank name and at least one item.
 */
export const requestFormValidationSchema = yup.object({
  name: yup
    .string()
    .trim()
    .required(i18next.t("purchaseRequests.nameRequired")),
  items: yup
    .array()
    .min(1, i18next.t("purchaseRequests.nameRequired"))
    .test(
      "all-items-complete",
      i18next.t("purchaseRequests.items.completeStatus"),
      (items?: ItemRow[]) => (items ?? []).every(isItemComplete)
    ),
});
